/**
 * yamazumiSyncService — Integração Yamazumi ↔ Execução
 *
 * Responsabilidades:
 * - Sincronizar planned_duration / planned_role_id nas tasks de uma instância
 * - Buscar carga real por cargo para o modo "Real" do Yamazumi
 * - Calcular métricas de eficiência planejado vs real
 */

import { supabase } from '../lib/supabaseClient';
import type { CardData } from '../types/card';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface YamazumiSyncActivity {
  /** ID do card no mapa de fluxo */
  cardId: string;
  /** Nome da atividade */
  name: string;
  /** Tempo planejado em minutos */
  plannedMinutes: number;
  /** UUID do cargo planejado */
  plannedRoleId: string | null;
}

export interface YamazumiSyncResult {
  synced: number;
  skipped: number;
  errors: string[];
}

export interface YamazumiRealLoad {
  /** UUID do cargo */
  roleId: string;
  /** Total de minutos reais medidos neste cargo */
  totalActualMinutes: number;
  /** Total de minutos planejados para este cargo */
  totalPlannedMinutes: number;
  /** Número de tasks */
  taskCount: number;
  /** Número de tarefas em gargalo (real > planejado) */
  bottleneckCount: number;
  /** Eficiência (0–1): planned / actual */
  efficiency: number | null;
  /** Tempo médio de fila em minutos */
  avgQueueMinutes: number | null;
}

export interface YamazumiEfficiencyKPIs {
  /** Eficiência global (0–1) */
  globalEfficiency: number | null;
  /** Total planejado em minutos */
  totalPlannedMinutes: number;
  /** Total real em minutos */
  totalActualMinutes: number;
  /** Número de gargalos (tasks com real > planejado) */
  bottleneckCount: number;
  /** Desvio em minutos (real - planejado) */
  deviationMinutes: number;
  /** Lead time real (maior carga por cargo) */
  leadTimeReal: number;
  /** Número de tasks medidas */
  measuredTasks: number;
}

export type EfficiencyClass = 'ok' | 'warning' | 'critical';

// ─────────────────────────────────────────────────────────────────────────────
// SYNC: Yamazumi → Tasks (planned_duration + planned_role_id)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Constrói a lista de atividades a partir dos cards do mapa + assignments do Yamazumi.
 * O Yamazumi não tem "distribuicao" — ele opera com CardData[] + assignments map.
 */
export function buildSyncActivities(
  cards: CardData[],
  assignments: Record<string, string>, // cardId → columnId (= roleId)
  roleIdByColumnId: Record<string, string | null>, // columnId → roleId
): YamazumiSyncActivity[] {
  return cards
    .filter((c) => c.tipo === 'atividade' && c.status === 'ativo')
    .map((card) => {
      const columnId = assignments[card.id] ?? null;
      const plannedRoleId = columnId ? (roleIdByColumnId[columnId] ?? null) : null;
      const plannedMinutes = getCycleTimeMinutes(card);

      return {
        cardId: card.id,
        name: card.atividade ?? card.nome_fluxo ?? card.id,
        plannedMinutes,
        plannedRoleId,
      };
    })
    .filter((a) => a.plannedMinutes > 0);
}

/**
 * Sincroniza planned_duration e planned_role_id nas tasks de uma instância.
 *
 * O vínculo entre card.id e workflow_task é feito via metadata.card_id
 * (campo salvo quando a tarefa é gerada a partir de um blueprint).
 */
export async function syncYamazumiToTasks(
  organizationId: string,
  instanceId: string | undefined,
  activities: YamazumiSyncActivity[],
): Promise<YamazumiSyncResult> {
  if (activities.length === 0) {
    return { synced: 0, skipped: 0, errors: [] };
  }

  // Buscar tasks da instância (ou todas da organização se instanceId não informado)
  let query = supabase
    .from('workflow_tasks_v9')
    .select('id, metadata, stage_name')
    .eq('organization_id', organizationId)
    .in('status', ['pending', 'in_progress', 'paused']);
  if (instanceId) {
    query = query.eq('instance_id', instanceId);
  }
  const { data: tasks, error: fetchError } = await query;

  if (fetchError) throw fetchError;
  if (!tasks || tasks.length === 0) return { synced: 0, skipped: activities.length, errors: [] };

  let synced = 0;
  let skipped = 0;
  const errors: string[] = [];

  for (const activity of activities) {
    // Tentar vincular por metadata.card_id primeiro, depois por stage_name / atividade
    const matched = tasks.find((t) => {
      const meta = (t.metadata ?? {}) as Record<string, unknown>;
      if (meta.card_id === activity.cardId) return true;
      // Fallback: nome da atividade vs nome do stage
      if (activity.name && t.stage_name) {
        return t.stage_name.toLowerCase().trim() === activity.name.toLowerCase().trim();
      }
      return false;
    });

    if (!matched) {
      skipped++;
      continue;
    }

    const { error: updateError } = await supabase
      .from('workflow_tasks_v9')
      .update({
        planned_duration: activity.plannedMinutes,
        planned_role_id: activity.plannedRoleId ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', matched.id)
      .eq('organization_id', organizationId);

    if (updateError) {
      errors.push(`task ${matched.id}: ${updateError.message}`);
    } else {
      synced++;
    }
  }

  return { synced, skipped, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH: Carga Real por Cargo (modo Real do Yamazumi)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Busca carga real por cargo para uma instância específica.
 * Usa a view vw_yamazumi_real_by_role ou consulta direta se a view não existir.
 */
export async function fetchRealLoadByRole(
  organizationId: string,
  instanceId: string,
): Promise<YamazumiRealLoad[]> {
  const { data, error } = await supabase
    .from('vw_yamazumi_real_by_role')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('instance_id', instanceId);

  if (error) {
    // Fallback: consulta direta se a view não existir
    return fetchRealLoadDirect(organizationId, instanceId);
  }

  return (data ?? []).map((row) => ({
    roleId: row.planned_role_id as string,
    totalActualMinutes: Number(row.total_actual_minutes ?? 0),
    totalPlannedMinutes: Number(row.total_planned_minutes ?? 0),
    taskCount: Number(row.task_count ?? 0),
    bottleneckCount: Number(row.bottleneck_count ?? 0),
    efficiency: row.efficiency_ratio != null ? Number(row.efficiency_ratio) : null,
    avgQueueMinutes: row.avg_queue_minutes != null ? Number(row.avg_queue_minutes) : null,
  }));
}

async function fetchRealLoadDirect(
  organizationId: string,
  instanceId: string,
): Promise<YamazumiRealLoad[]> {
  const { data, error } = await supabase
    .from('workflow_tasks_v9')
    .select('planned_role_id, actual_duration, planned_duration, queue_duration')
    .eq('organization_id', organizationId)
    .eq('instance_id', instanceId)
    .eq('status', 'completed')
    .not('planned_role_id', 'is', null)
    .not('actual_duration', 'is', null);

  if (error) throw error;

  const byRole: Record<string, YamazumiRealLoad> = {};

  for (const row of data ?? []) {
    const roleId = row.planned_role_id as string;
    if (!byRole[roleId]) {
      byRole[roleId] = {
        roleId,
        totalActualMinutes: 0,
        totalPlannedMinutes: 0,
        taskCount: 0,
        bottleneckCount: 0,
        efficiency: null,
        avgQueueMinutes: null,
      };
    }
    const entry = byRole[roleId];
    const actual = Number(row.actual_duration ?? 0);
    const planned = Number(row.planned_duration ?? 0);
    const queue = Number(row.queue_duration ?? 0);

    entry.totalActualMinutes += actual;
    entry.totalPlannedMinutes += planned;
    entry.taskCount++;
    if (actual > planned) entry.bottleneckCount++;
    entry.avgQueueMinutes = (entry.avgQueueMinutes ?? 0) + queue;
  }

  // Finalizar médias e eficiências
  for (const entry of Object.values(byRole)) {
    if (entry.avgQueueMinutes !== null && entry.taskCount > 0) {
      entry.avgQueueMinutes = entry.avgQueueMinutes / entry.taskCount;
    }
    entry.efficiency =
      entry.totalActualMinutes > 0
        ? entry.totalPlannedMinutes / entry.totalActualMinutes
        : null;
  }

  return Object.values(byRole);
}

// ─────────────────────────────────────────────────────────────────────────────
// KPIs GLOBAIS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calcula KPIs de eficiência global a partir das cargas por cargo.
 */
export function calcEfficiencyKPIs(loads: YamazumiRealLoad[]): YamazumiEfficiencyKPIs {
  const totalPlanned = loads.reduce((s, l) => s + l.totalPlannedMinutes, 0);
  const totalActual = loads.reduce((s, l) => s + l.totalActualMinutes, 0);
  const bottleneckCount = loads.reduce((s, l) => s + l.bottleneckCount, 0);
  const measuredTasks = loads.reduce((s, l) => s + l.taskCount, 0);
  const leadTimeReal = loads.reduce((s, l) => Math.max(s, l.totalActualMinutes), 0);

  return {
    globalEfficiency: totalActual > 0 ? totalPlanned / totalActual : null,
    totalPlannedMinutes: totalPlanned,
    totalActualMinutes: totalActual,
    bottleneckCount,
    deviationMinutes: totalActual - totalPlanned,
    leadTimeReal,
    measuredTasks,
  };
}

export interface KPIBarData {
  /** Eficiência global (0–1), null se sem dados */
  eficiencia: number | null;
  /** Número de tarefas com actual > planned */
  gargalos: number;
  /** Fila média global em minutos, null se sem dados */
  filaMedia: number | null;
  /** Total de tarefas medidas */
  measuredTasks: number;
}

/**
 * Calcula os KPIs de alto nível para exibição na KPIBar.
 *
 * - eficiencia = sum(planned) / sum(actual)
 * - gargalos   = tasks onde actual > planned
 * - filaMedia  = média ponderada de avgQueueMinutes por cargo
 */
export function calcularKPIs(loads: YamazumiRealLoad[]): KPIBarData {
  if (loads.length === 0) {
    return { eficiencia: null, gargalos: 0, filaMedia: null, measuredTasks: 0 };
  }

  const totalPlanned = loads.reduce((s, l) => s + l.totalPlannedMinutes, 0);
  const totalActual = loads.reduce((s, l) => s + l.totalActualMinutes, 0);
  const gargalos = loads.reduce((s, l) => s + l.bottleneckCount, 0);
  const measuredTasks = loads.reduce((s, l) => s + l.taskCount, 0);

  // Fila média global: média ponderada por taskCount
  const loadsWithQueue = loads.filter((l) => l.avgQueueMinutes !== null && l.taskCount > 0);
  const filaMedia = loadsWithQueue.length > 0
    ? loadsWithQueue.reduce((s, l) => s + (l.avgQueueMinutes ?? 0) * l.taskCount, 0)
      / loadsWithQueue.reduce((s, l) => s + l.taskCount, 0)
    : null;

  return {
    eficiencia: totalActual > 0 ? totalPlanned / totalActual : null,
    gargalos,
    filaMedia: filaMedia !== null ? Math.round(filaMedia) : null,
    measuredTasks,
  };
}

/**
 * Classifica eficiência em ok / warning / critical.
 */
export function classifyEfficiency(efficiency: number | null): EfficiencyClass {
  if (efficiency === null) return 'ok';
  if (efficiency >= 0.95) return 'ok';
  if (efficiency >= 0.80) return 'warning';
  return 'critical';
}

/**
 * Calcula eficiência por tarefa individual.
 */
export function calcTaskEfficiency(
  plannedDuration: number | null,
  actualDuration: number | null,
): { efficiency: number | null; deviation: number | null; class: EfficiencyClass } {
  if (!plannedDuration || !actualDuration) {
    return { efficiency: null, deviation: null, class: 'ok' };
  }
  const efficiency = plannedDuration / actualDuration;
  const deviation = actualDuration - plannedDuration;
  return { efficiency, deviation, class: classifyEfficiency(efficiency) };
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-BALANCEAMENTO
// ─────────────────────────────────────────────────────────────────────────────

export interface BalanceInput {
  cardId: string;
  name: string;
  minutes: number;
}

export interface BalanceColumn {
  columnId: string;
  capacity: number; // minutos/turno
  currentLoad: number;
}

/**
 * Redistribui atividades nas colunas usando greedy bin-packing (menor carga primeiro).
 * Retorna novo mapa assignments: cardId → columnId.
 */
export function autoBalance(
  activities: BalanceInput[],
  columns: BalanceColumn[],
): Record<string, string> {
  const sorted = [...activities].sort((a, b) => b.minutes - a.minutes);
  const loads: Record<string, number> = Object.fromEntries(
    columns.map((c) => [c.columnId, c.currentLoad]),
  );
  const result: Record<string, string> = {};

  for (const activity of sorted) {
    // Encontrar coluna com menor carga atual
    const target = columns.reduce((min, col) =>
      (loads[col.columnId] ?? 0) < (loads[min.columnId] ?? 0) ? col : min,
    );
    result[activity.cardId] = target.columnId;
    loads[target.columnId] = (loads[target.columnId] ?? 0) + activity.minutes;
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITÁRIOS
// ─────────────────────────────────────────────────────────────────────────────

export function getCycleTimeMinutes(card: CardData): number {
  // Preferência: tt (takt time / cycle time) → tp (process time) → lt (lead time)
  const raw = card.tt ?? card.tp ?? card.lt ?? 0;
  const value = typeof raw === 'string' ? parseFloat(raw) : (raw ?? 0);
  if (!isFinite(value) || value <= 0) return 0;

  // Converter unidades se tpUnit informado
  switch (card.tpUnit) {
    case 'segundos': return value / 60;
    case 'horas':    return value * 60;
    case 'dias':     return value * 480; // 8h/dia
    default:         return value; // minutos (padrão)
  }
}

export function formatEfficiencyPct(efficiency: number | null): string {
  if (efficiency === null) return '—';
  return `${Math.round(efficiency * 100)}%`;
}

export function formatDeviationMinutes(deviation: number | null): string {
  if (deviation === null) return '—';
  const sign = deviation > 0 ? '+' : '';
  return `${sign}${Math.round(deviation)}min`;
}
